import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Save, Edit2, Trash2, UserCheck, CheckSquare, CheckCircle,
  Download, Upload, Camera, Mail, Loader2, Package, Check,
  Send, Settings, RefreshCw, MailCheck, AlertCircle,
  Image as ImageIcon, GripVertical, X, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { getRsvpTypeColor, getPaymentStatusColor, getRsvpStatusColor, getCheckinColor } from '../../../utils/statusColors';
import CheckInScanner from './CheckInScanner';
import CheckInLogPanel from './CheckInLogPanel';
import MediaPicker from '../../../components/admin/MediaPicker';
import PublishToGalleryModal from '../../../components/admin/PublishToGalleryModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Universal Media Standard — Phase C: max 12 photos TOTAL per event
// (flyer/featured counts as one of the 12). Legacy events with more
// photos read/render unchanged.
const EVENT_MAX_TOTAL_PHOTOS = 12;

const ItemCommitmentsPanel = ({ events }) => {
  const [selectedEventId, setSelectedEventId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const publishedEvents = events.filter(e => e.is_published);

  const fetchCommitments = useCallback(async (eid) => {
    if (!eid) { setData(null); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/events/${eid}/item-commitments`, { withCredentials: true });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, []);

  useEffect(() => { if (selectedEventId) fetchCommitments(selectedEventId); }, [selectedEventId, fetchCommitments]);

  const updateStatus = async (commitmentId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/events/${selectedEventId}/item-commitments/${commitmentId}`,
        { status: newStatus }, { withCredentials: true });
      fetchCommitments(selectedEventId);
    } catch { alert('Update failed'); }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="item-commitments-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-[#BA1826]" />
          <h3 className="font-heading font-bold text-sm text-[#0A0A0A]">Item Commitments</h3>
        </div>
        <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)}
          className="text-xs border border-[#E5E7EB] rounded px-2 py-1.5 focus:border-[#BA1826] focus:outline-none"
          data-testid="commitments-event-select">
          <option value="">Select event...</option>
          {publishedEvents.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
        </select>
      </div>

      {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>}

      {!loading && !selectedEventId && (
        <p className="text-xs text-[#9CA3AF] text-center py-4">Select an event to view item commitments from RSVPs.</p>
      )}

      {!loading && selectedEventId && data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-xs text-[#9CA3AF] text-center py-4">No item commitments for this event yet.</p>
          ) : (
            <div className="space-y-3">
              {data.items.map((item) => {
                const progress = item.total_committed > 0 ? Math.round((item.total_received / item.total_committed) * 100) : 0;
                return (
                  <div key={item.item_name} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`commitment-group-${item.item_name.replace(/\s/g, '-').toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-bold text-[#0A0A0A]">{item.item_name}</h4>
                      <span className="text-[10px] font-semibold text-[#BA1826]">
                        {item.total_received} / {item.total_committed} received
                      </span>
                    </div>
                    <div className="w-full bg-[#F1F5F9] rounded-full h-1.5 mb-2">
                      <div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="space-y-1.5">
                      {item.attendees.map((a) => (
                        <div key={a.id} className="flex items-center justify-between text-[10px]">
                          <span className="text-[#4B5563]">{a.attendee_name} — {a.quantity} item{a.quantity !== 1 ? 's' : ''}</span>
                          <div className="flex items-center gap-1.5">
                            <span className={`px-1.5 py-0.5 rounded font-semibold ${
                              a.status === 'received' ? 'bg-green-50 text-green-700' :
                              a.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'
                            }`}>{a.status}</span>
                            {a.status !== 'received' && (
                              <button onClick={() => updateStatus(a.id, 'received')}
                                className="text-green-600 hover:text-green-800 font-semibold flex items-center gap-0.5"
                                data-testid={`mark-received-${a.id}`}>
                                <Check className="w-3 h-3" /> Received
                              </button>
                            )}
                            {a.status === 'received' && (
                              <button onClick={() => updateStatus(a.id, 'expected')}
                                className="text-[#9CA3AF] hover:text-[#4B5563] font-semibold">
                                Undo
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ========== Send Message Panel ========== */
const SendMessagePanel = ({ eventId }) => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!subject.trim() || !message.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/${eventId}/send-message`,
        { subject, message }, { withCredentials: true });
      setResult(res.data);
      setSubject(''); setMessage('');
    } catch (e) {
      setResult({ message: e.response?.data?.detail || 'Failed to send' });
    }
    setSending(false);
  };

  return (
    <div className="mt-4 border-t border-[#E5E7EB] pt-4">
      <button onClick={() => { setOpen(!open); setResult(null); }}
        className="text-xs text-[#BA1826] font-semibold flex items-center gap-1"
        data-testid={`send-message-toggle-${eventId}`}>
        <Send className="w-3.5 h-3.5" /> {open ? 'Close Message Panel' : 'Send Message to Attendees'}
      </button>
      {open && (
        <div className="mt-3 bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid={`send-message-panel-${eventId}`}>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject line..."
            className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
            data-testid={`message-subject-${eventId}`} />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Message body..."
            rows={4}
            className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none resize-none"
            data-testid={`message-body-${eventId}`} />
          <div className="flex items-center justify-between">
            <button onClick={handleSend} disabled={sending || !subject.trim() || !message.trim()}
              className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40"
              data-testid={`send-message-btn-${eventId}`}>
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              {sending ? 'Sending...' : 'Send to All Attendees'}
            </button>
            {result && (
              <span className="text-xs text-green-700 font-medium" data-testid={`send-result-${eventId}`}>{result.message}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ========== Email Settings Panel ========== */
const EmailSettingsPanel = ({ eventId }) => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({ rsvp_email_enabled: true, rsvp_email_subject: '', rsvp_email_body: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/events/${eventId}/email-settings`, { withCredentials: true });
      setSettings(res.data);
    } catch {}
  }, [eventId]);

  useEffect(() => { if (open) fetchSettings(); }, [open, fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/events/${eventId}/email-settings`, settings, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="mt-3 border-t border-[#E5E7EB] pt-3">
      <button onClick={() => setOpen(!open)}
        className="text-xs text-[#4B5563] font-semibold flex items-center gap-1 hover:text-[#BA1826]"
        data-testid={`email-settings-toggle-${eventId}`}>
        <Settings className="w-3.5 h-3.5" /> {open ? 'Close Email Settings' : 'RSVP Email Settings'}
      </button>
      {open && (
        <div className="mt-3 bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid={`email-settings-panel-${eventId}`}>
          <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
            <input type="checkbox" checked={settings.rsvp_email_enabled}
              onChange={(e) => setSettings(s => ({ ...s, rsvp_email_enabled: e.target.checked }))}
              className="accent-[#BA1826]" data-testid={`email-enabled-toggle-${eventId}`} />
            {settings.rsvp_email_enabled ? 'RSVP Emails ON' : 'RSVP Emails OFF'}
          </label>
          {settings.rsvp_email_enabled && (
            <>
              <div>
                <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Custom Subject (leave blank for default)</label>
                <input type="text" value={settings.rsvp_email_subject}
                  onChange={(e) => setSettings(s => ({ ...s, rsvp_email_subject: e.target.value }))}
                  placeholder="Your Entry Pass: {event title}"
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none"
                  data-testid={`email-subject-${eventId}`} />
              </div>
              <div>
                <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Custom Message (leave blank for default)</label>
                <textarea value={settings.rsvp_email_body}
                  onChange={(e) => setSettings(s => ({ ...s, rsvp_email_body: e.target.value }))}
                  placeholder="Thank you for wanting to attend..."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:border-[#BA1826] focus:outline-none resize-none"
                  data-testid={`email-body-${eventId}`} />
              </div>
            </>
          )}
          <button onClick={handleSave} disabled={saving}
            className="text-xs font-semibold px-3 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-white flex items-center gap-1 disabled:opacity-50"
            data-testid={`save-email-settings-${eventId}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <Check className="w-3 h-3 text-green-600" /> : <Save className="w-3 h-3" />}
            {saved ? 'Saved!' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * EventPhotoStoryEditor — shared component for editing an event's
 * curated photo story (event_photos array). Enforces the Universal
 * Media Standard 12-total cap client-side (backend also validates
 * with HTTP 422). `flyerUrl` counts as one of the 12 when present.
 * Supports: direct-from-device upload, Media Library pick, drag-to-
 * reorder, remove. Reordering just mutates the parent array — the
 * event PUT persists it.
 */
const EventPhotoStoryEditor = ({
  flyerUrl,
  photos,
  onChange,
  testIdPrefix = 'event-photos',
  eventId = null,
  eventTitle = null,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [publishOpen, setPublishOpen] = useState(false);
  const fileRef = useRef(null);

  const flyerCount = (flyerUrl || '').trim() ? 1 : 0;
  const additionalMax = EVENT_MAX_TOTAL_PHOTOS - flyerCount;
  const count = (photos || []).length;
  const remaining = Math.max(0, additionalMax - count);
  const atCap = remaining === 0;
  const overCap = count > additionalMax;

  const uploadOne = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.url;
  };

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = '';
    if (files.length === 0) return;
    if (remaining === 0) return;
    const clipped = files.slice(0, remaining);
    const trimmedNote = files.length > remaining
      ? ` Only the first ${remaining} were uploaded (${EVENT_MAX_TOTAL_PHOTOS}-photo total limit).`
      : '';
    setUploading(true); setUploadError('');
    const urls = [];
    try {
      for (const f of clipped) {
        try {
          const u = await uploadOne(f);
          if (u) urls.push(u);
        } catch (err) {
          setUploadError(err.response?.data?.detail || err.message || 'Upload failed.');
        }
      }
      if (urls.length) onChange([...(photos || []), ...urls]);
      if (trimmedNote) setUploadError(trimmedNote.trim());
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const next = [...(photos || [])];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setDragIdx(null); setDragOverIdx(null);
    onChange(next);
  };

  const removeAt = (idx) => {
    const next = [...(photos || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-[#0A0A0A] block">
          Event Photos ({count}/{additionalMax})
          {flyerCount === 1 && (
            <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">
              · flyer counts as 1 of {EVENT_MAX_TOTAL_PHOTOS}
            </span>
          )}
        </label>
        <div className="flex items-center gap-1.5">
          {eventId && (photos || []).length > 0 && (
            <button
              type="button"
              onClick={() => setPublishOpen(true)}
              className="text-[11px] px-2 py-1 border border-[#BA1826] text-[#BA1826] rounded inline-flex items-center gap-1 hover:bg-[#FEF2F2]"
              data-testid={`${testIdPrefix}-publish-btn`}
              title="Publish selected photos to the public Main Gallery">
              <ImageIcon className="w-3 h-3" /> Publish to Main Gallery
            </button>
          )}
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={atCap || overCap}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1 hover:bg-[#F8F9FA] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testIdPrefix}-pick-library`}>
            <ImageIcon className="w-3 h-3" /> Media Library
          </button>
          <label className={`text-[11px] px-2 py-1 rounded inline-flex items-center gap-1 border ${atCap || overCap ? 'bg-[#F8F9FA] border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed' : 'bg-[#BA1826] border-[#BA1826] text-white cursor-pointer hover:bg-[#9E1420]'}`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            Upload
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              disabled={atCap || overCap || uploading}
              onChange={handleFileInput}
              data-testid={`${testIdPrefix}-upload-input`}
            />
          </label>
        </div>
      </div>
      {overCap && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-[11px] p-2 rounded inline-flex items-start gap-1.5 mb-2" data-testid={`${testIdPrefix}-over-cap`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <strong>Legacy event with {count} additional photos.</strong>{' '}
            Public renders unchanged, but the new standard caps at {EVENT_MAX_TOTAL_PHOTOS} total.
            Remove photos to bring this in line — you can&apos;t add more until you do.
          </div>
        </div>
      )}
      {uploadError && (
        <p className="text-[11px] text-[#B91C1C] mt-1" data-testid={`${testIdPrefix}-error`}>
          {uploadError}
        </p>
      )}
      {(photos || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid={`${testIdPrefix}-grid`}>
          {photos.map((p, idx) => (
            <div
              key={`${p}-${idx}`}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
              onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              onDrop={() => handleDrop(idx)}
              className={`relative w-20 h-20 rounded overflow-hidden group border-2 ${dragOverIdx === idx ? 'border-[#BA1826]' : 'border-transparent'} cursor-move`}
              data-testid={`${testIdPrefix}-item-${idx}`}>
              <img src={p} alt="" className="w-full h-full object-cover pointer-events-none" />
              <div className="absolute top-0.5 left-0.5 bg-black/60 text-white rounded p-0.5">
                <GripVertical className="w-2.5 h-2.5" />
              </div>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testIdPrefix}-remove-${idx}`}
                aria-label="Remove photo">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        mode="multi"
        excludeUrls={photos || []}
        onPick={(url) => {
          if (remaining <= 0) return;
          onChange([...(photos || []), url].slice(0, additionalMax));
        }}
      />
      {eventId && (
        <PublishToGalleryModal
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
          sourceType="event"
          sourceId={eventId}
          defaultAlbumSlug="events"
          photos={(photos || []).map((url) => ({
            id: url,
            image_url: url,
            thumb_url: url,
            title: eventTitle || null,
          }))}
        />
      )}
    </div>
  );
};




const AdminEventsTab = ({ ctx }) => {
  const [resendingId, setResendingId] = useState(null);
  const [emailFilter, setEmailFilter] = useState('all');
  const [bulkResending, setBulkResending] = useState(false);
  const [sendingConfCheck, setSendingConfCheck] = useState(false);

  const {
    events, eventForm, setEventForm, editingEvent,
    eventAttendees, viewingEventId, setViewingEventId,
    allAttendees, showAllAttendees,
    completingEventId, setCompletingEventId, completeForm, setCompleteForm,
    uploadingFor,
    attendeeSearch, setAttendeeSearch, attendeeEventFilter, setAttendeeEventFilter,
    saveEvent, deleteEvent, toggleEventActive, loadAttendees, toggleCheckIn,
    handleFileUpload, startCompleteEvent, submitCompleteEvent,
    loadAllAttendees, exportAttendeesCsv, sendReminders, resetEventForm,
    fetchData
  } = ctx;

  const handleResendEmail = async (attendeeId) => {
    setResendingId(attendeeId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/attendees/${attendeeId}/resend-email`, {}, { withCredentials: true });
      if (res.data.success) {
        alert('Email resent successfully!');
      } else {
        alert(`Failed: ${res.data.error || 'Unknown error'}`);
      }
      loadAllAttendees();
    } catch (e) {
      alert('Failed to resend email');
    }
    setResendingId(null);
  };

  const handleBulkResendFailed = async () => {
    if (!window.confirm('Resend RSVP emails to all failed/undelivered attendees?')) return;
    setBulkResending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/attendees/bulk-resend-failed`, { event_id: attendeeEventFilter || '' }, { withCredentials: true });
      alert(`Sent: ${res.data.sent}, Failed: ${res.data.failed}, Total: ${res.data.total}`);
      loadAllAttendees();
    } catch { alert('Bulk resend failed'); }
    setBulkResending(false);
  };

  const handleSendConfirmationCheck = async () => {
    if (!window.confirm('Send "confirm receipt" follow-up email to all attendees?')) return;
    setSendingConfCheck(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/events/attendees/send-confirmation-check`, { event_id: attendeeEventFilter || '' }, { withCredentials: true });
      alert(`Sent to ${res.data.sent} of ${res.data.total} attendees`);
    } catch { alert('Failed to send confirmation check'); }
    setSendingConfCheck(false);
  };

  const getEmailStatusBadge = (a) => {
    const status = a.email_status || (a.confirmation_sent ? 'sent' : 'pending');
    const confirmed = a.email_confirmed;
    if (confirmed) return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700" title={`Confirmed at ${a.email_confirmed_at || ''}`}>Confirmed</span>;
    if (status === 'delivered') return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700">Delivered</span>;
    if (status === 'sent') return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-100 text-blue-700">Sent</span>;
    if (status === 'opened') return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700">Opened</span>;
    if (status === 'bounced') return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-700">Bounced</span>;
    if (status === 'failed') return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-700">Failed</span>;
    if (status === 'complained') return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-orange-100 text-orange-700">Spam</span>;
    if (status === 'pending') return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-700">Pending</span>;
    return <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 text-gray-500">Unknown</span>;
  };

  return (
    <div className="space-y-6">
      {/* Check-In Scanner */}
      <CheckInScanner events={events} onCheckedIn={() => { loadAllAttendees(); }} />

      {/* Global Attendee Management */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="attendee-management">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-heading font-bold text-sm flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-[#BA1826]" /> Attendee Management
          </h3>
          <div className="flex gap-2 flex-wrap">
            <button onClick={loadAllAttendees} className="text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded px-3 py-1.5 hover:bg-[#F1F5F9]" data-testid="load-all-attendees-btn">
              All Attendees
            </button>
            <button onClick={() => exportAttendeesCsv(attendeeEventFilter)} className="text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded px-3 py-1.5 hover:bg-[#F1F5F9] flex items-center gap-1" data-testid="export-csv-btn">
              <Download className="w-3 h-3" /> Export CSV
            </button>
            <button onClick={handleBulkResendFailed} disabled={bulkResending}
              className="text-xs bg-[#FEF2F2] border border-[#FECACA] text-[#BA1826] rounded px-3 py-1.5 hover:bg-[#FEE2E2] flex items-center gap-1 disabled:opacity-50"
              data-testid="bulk-resend-failed-btn">
              {bulkResending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Resend Failed
            </button>
            <button onClick={handleSendConfirmationCheck} disabled={sendingConfCheck}
              className="text-xs bg-[#F0F9FF] border border-[#BAE6FD] text-[#0369A1] rounded px-3 py-1.5 hover:bg-[#E0F2FE] flex items-center gap-1 disabled:opacity-50"
              data-testid="send-confirmation-check-btn">
              {sendingConfCheck ? <Loader2 className="w-3 h-3 animate-spin" /> : <MailCheck className="w-3 h-3" />} Confirm Check
            </button>
          </div>
        </div>
        {showAllAttendees && (
          <div>
            <div className="flex gap-2 mb-3 flex-wrap">
              <input type="text" placeholder="Search by name, email, or Entry ID..." value={attendeeSearch}
                onChange={(e) => setAttendeeSearch(e.target.value)}
                className="flex-1 min-w-[180px] px-3 py-2 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
                data-testid="attendee-search-input" />
              <select value={attendeeEventFilter} onChange={(e) => setAttendeeEventFilter(e.target.value)}
                className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
                data-testid="attendee-event-filter">
                <option value="">All Events</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
              </select>
              <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)}
                className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
                data-testid="email-status-filter">
                <option value="all">All Email Status</option>
                <option value="failed">Failed / Not Delivered</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="confirmed">Confirmed Receipt</option>
                <option value="bounced">Bounced</option>
              </select>
            </div>
            {(() => {
              const filtered = allAttendees.filter(a => {
                const matchSearch = !attendeeSearch ||
                  a.name?.toLowerCase().includes(attendeeSearch.toLowerCase()) ||
                  a.email?.toLowerCase().includes(attendeeSearch.toLowerCase()) ||
                  a.entry_id?.toLowerCase().includes(attendeeSearch.toLowerCase());
                const matchEvent = !attendeeEventFilter || a.event_id === attendeeEventFilter;
                const eStatus = a.email_status || (a.confirmation_sent ? 'sent' : 'pending');
                let matchEmail = true;
                if (emailFilter === 'failed') matchEmail = ['failed', 'bounced', 'not_delivered'].includes(eStatus) || (!a.email_status && !a.confirmation_sent);
                else if (emailFilter === 'sent') matchEmail = eStatus === 'sent';
                else if (emailFilter === 'delivered') matchEmail = eStatus === 'delivered' || eStatus === 'opened';
                else if (emailFilter === 'confirmed') matchEmail = !!a.email_confirmed;
                else if (emailFilter === 'bounced') matchEmail = eStatus === 'bounced';
                return matchSearch && matchEvent && matchEmail;
              });
              const checkedIn = filtered.filter(a => a.checked_in).length;
              return (
                <>
                  <div className="flex items-center gap-3 mb-2 text-xs text-[#4B5563]">
                    <span>{filtered.length} attendees</span>
                    <span className="text-green-600 font-semibold">{checkedIn} checked in</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-[#E5E7EB] rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F8F9FA] sticky top-0">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Entry ID</th>
                          <th className="text-left p-2">Email</th>
                          <th className="text-left p-2">Event</th>
                          <th className="text-left p-2">Type</th>
                          <th className="text-left p-2">Payment</th>
                          <th className="text-left p-2">Amount</th>
                          <th className="text-center p-2">Email Status</th>
                          <th className="text-center p-2">Check-in</th>
                          <th className="text-center p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(a => (
                          <tr key={a.id} className="border-t border-[#F1F5F9]">
                            <td className="p-2 font-medium">{a.name}</td>
                            <td className="p-2">
                              {a.entry_id ? (
                                <span className="font-mono text-[10px] bg-[#F1F5F9] text-[#4B5563] px-1.5 py-0.5 rounded">{a.entry_id}</span>
                              ) : <span className="text-[#D1D5DB]">—</span>}
                            </td>
                            <td className="p-2 text-[#4B5563]">
                              {a.email}
                              {a.email_flagged && <span className="ml-1 text-[8px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-semibold">Invalid</span>}
                              {a.email_excluded && <span className="ml-1 text-[8px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded font-semibold">Excluded</span>}
                            </td>
                            <td className="p-2 text-[#4B5563]">{a.event_title}</td>
                            <td className="p-2">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${getRsvpTypeColor(a.rsvp_type)}`}>{a.rsvp_type || 'free'}</span>
                            </td>
                            <td className="p-2">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                                a.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                a.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                a.payment_status === 'free' ? 'bg-gray-100 text-gray-500' : 'bg-gray-100 text-gray-500'
                              }`}>{a.payment_status || 'free'}</span>
                            </td>
                            <td className="p-2 text-[#4B5563]">
                              {(a.ticket_price_paid > 0 || a.donation_amount > 0) ? (
                                <span className="font-semibold text-green-700">${a.ticket_price_paid || a.donation_amount || 0}</span>
                              ) : (
                                <span className="text-[#9CA3AF]">—</span>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              {getEmailStatusBadge(a)}
                            </td>
                            <td className="p-2 text-center">
                              <button onClick={async () => {
                                await axios.put(`${BACKEND_URL}/api/admin/events/attendees/${a.id}`, { checked_in: !a.checked_in }, { withCredentials: true });
                                loadAllAttendees();
                              }} className={`w-5 h-5 rounded border-2 inline-flex items-center justify-center ${a.checked_in ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'}`}>
                                {a.checked_in && <CheckSquare className="w-3 h-3" />}
                              </button>
                            </td>
                            <td className="p-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => handleResendEmail(a.id)} disabled={resendingId === a.id || !a.email || !a.entry_id}
                                  className="text-blue-500 hover:text-blue-700 disabled:opacity-30" title="Resend email"
                                  data-testid={`resend-email-${a.id}`}>
                                  {resendingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                </button>
                                <button onClick={async () => {
                                  if (!window.confirm(`Delete ${a.name}? This cannot be undone.`)) return;
                                  try {
                                    await axios.delete(`${BACKEND_URL}/api/admin/events/attendees/${a.id}`, { withCredentials: true });
                                    loadAllAttendees();
                                  } catch { alert('Delete failed'); }
                                }}
                                  className="text-red-400 hover:text-red-600"
                                  title="Delete attendee"
                                  data-testid={`delete-global-attendee-${a.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Check-In Log — live-updating dedicated arrival dashboard */}
      <CheckInLogPanel events={events} />

      {/* Item Commitments Tracking */}
      <ItemCommitmentsPanel events={events} />


      {/* Completion Modal */}
      {completingEventId && (
        <div className="bg-white border-2 border-[#BA1826] rounded-lg p-5" data-testid="complete-event-form">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-[#BA1826]" />
            <h3 className="font-heading font-bold">Mark Event as Completed</h3>
          </div>
          <div className="space-y-3 mb-4">
            <textarea placeholder="Event recap — what happened, key highlights..." value={completeForm.recap_text}
              onChange={(e) => setCompleteForm({...completeForm, recap_text: e.target.value})} rows={3}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none resize-none"
              data-testid="complete-recap-input" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Funds Raised ($)</label>
                <input type="number" min="0" step="0.01" value={completeForm.funds_raised}
                  onChange={(e) => setCompleteForm({...completeForm, funds_raised: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="complete-funds-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Candles Lit</label>
                <input type="number" min="0" value={completeForm.candles_lit}
                  onChange={(e) => setCompleteForm({...completeForm, candles_lit: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="complete-candles-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Attendees</label>
                <input type="number" min="0" value={completeForm.total_attendees}
                  onChange={(e) => setCompleteForm({...completeForm, total_attendees: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="complete-attendees-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Items Collected</label>
                <input type="number" min="0" value={completeForm.donation_items_collected}
                  onChange={(e) => setCompleteForm({...completeForm, donation_items_collected: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="complete-items-input" />
              </div>
            </div>
            <div>
              {/* Post-event recap photos — Universal Media Standard Phase C.
                  The event's stored flyer_image still counts as one of the 12.
                  We look it up from the events list so the counter is accurate. */}
              <EventPhotoStoryEditor
                flyerUrl={(events.find(x => x.id === completingEventId) || {}).flyer_image || ''}
                photos={completeForm.event_photos || []}
                onChange={(next) => setCompleteForm(prev => ({ ...prev, event_photos: next }))}
                testIdPrefix="complete-photos"
                eventId={completingEventId}
                eventTitle={(events.find(x => x.id === completingEventId) || {}).title}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={submitCompleteEvent} className="btn-primary text-sm" data-testid="submit-complete-btn">
              <CheckCircle className="w-4 h-4" /> Mark as Completed
            </button>
            <button onClick={() => setCompletingEventId(null)} className="btn-secondary text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Create/Edit Event Form */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="event-form">
        <h3 className="font-heading font-bold mb-4">{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
        <div className="space-y-3 mb-4">
          <input type="text" placeholder="Event title" value={eventForm.title}
            onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
            data-testid="event-title-input" />
          <textarea placeholder="Event description" value={eventForm.description}
            onChange={(e) => setEventForm({...eventForm, description: e.target.value})} rows={3}
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none resize-none"
            data-testid="event-description-input" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <input type="text" placeholder="Date (e.g. April 20, 2026)" value={eventForm.date}
              onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
              className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="event-date-input" />
            <input type="text" placeholder="Time (e.g. 6:00 PM)" value={eventForm.time}
              onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
              className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="event-time-input" />
            <input type="text" placeholder="Location" value={eventForm.location}
              onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
              className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="event-location-input" />
          </div>
          {/* Flyer Image with Upload */}
          <div>
            <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Event Flyer</label>
            <div className="flex gap-2">
              <input type="text" placeholder="Flyer image URL (or upload)" value={eventForm.flyer_image}
                onChange={(e) => setEventForm({...eventForm, flyer_image: e.target.value})}
                className="flex-1 px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="event-flyer-input" />
              <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs bg-[#F8F9FA] border border-[#E5E7EB] rounded px-3 py-2 hover:bg-[#F1F5F9] transition-colors flex-shrink-0">
                <Upload className="w-3.5 h-3.5" />
                {uploadingFor === 'flyer' ? 'Uploading...' : 'Upload'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'flyer')} disabled={uploadingFor === 'flyer'} />
              </label>
            </div>
            {eventForm.flyer_image && (
              <img src={eventForm.flyer_image} alt="Flyer preview" className="w-20 h-20 object-cover rounded mt-2 border border-[#E5E7EB]" />
            )}
          </div>
          {/* Event Photos Upload — Universal Media Standard Phase C */}
          <EventPhotoStoryEditor
            flyerUrl={eventForm.flyer_image}
            photos={eventForm.event_photos || []}
            onChange={(next) => setEventForm(prev => ({ ...prev, event_photos: next }))}
            testIdPrefix="event-photos"
            eventId={editingEvent ? (editingEvent.id || editingEvent) : null}
            eventTitle={eventForm.title}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Ticket Type</label>
              <select value={eventForm.ticket_type}
                onChange={(e) => setEventForm({...eventForm, ticket_type: e.target.value, ticket_price: e.target.value === 'free' ? 0 : eventForm.ticket_price})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="event-ticket-type">
                <option value="free">Free RSVP</option>
                <option value="paid">Paid Ticket (Stripe)</option>
              </select>
            </div>
            {eventForm.ticket_type === 'paid' && (
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Ticket Price ($)</label>
                <input type="number" min="0" step="0.01" value={eventForm.ticket_price}
                  onChange={(e) => setEventForm({...eventForm, ticket_price: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="event-price-input" />
              </div>
            )}
          </div>
          <textarea placeholder="Confirmation message shown after RSVP" value={eventForm.confirmation_message}
            onChange={(e) => setEventForm({...eventForm, confirmation_message: e.target.value})} rows={2}
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none resize-none" />
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={eventForm.is_active}
                onChange={(e) => setEventForm({...eventForm, is_active: e.target.checked, is_published: e.target.checked ? true : eventForm.is_published})}
                data-testid="event-active-checkbox" />
              Active (QR points here)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={eventForm.is_published}
                onChange={(e) => setEventForm({...eventForm, is_published: e.target.checked})}
                data-testid="event-published-checkbox" />
              Published
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={eventForm.rsvp_enabled}
                onChange={(e) => setEventForm({...eventForm, rsvp_enabled: e.target.checked})} />
              RSVP Enabled
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Max Capacity (0 = unlimited)</label>
              <input type="number" min="0" value={eventForm.max_capacity}
                onChange={(e) => setEventForm({...eventForm, max_capacity: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="event-capacity-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">VIP Price ($)</label>
              <input type="number" min="0" step="0.01" value={eventForm.vip_price}
                onChange={(e) => setEventForm({...eventForm, vip_price: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="event-vip-price-input" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={eventForm.donation_rsvp_enabled}
                onChange={(e) => setEventForm({...eventForm, donation_rsvp_enabled: e.target.checked})}
                data-testid="event-donation-rsvp-checkbox" />
              Donation RSVP option
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={eventForm.vip_rsvp_enabled}
                onChange={(e) => setEventForm({...eventForm, vip_rsvp_enabled: e.target.checked})}
                data-testid="event-vip-rsvp-checkbox" />
              VIP / Supporter option
            </label>
          </div>

          {/* ========== DONATION TIER PRESETS ========== */}
          {eventForm.donation_rsvp_enabled && (
            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4" data-testid="donation-presets-section">
              <h4 className="text-xs font-bold text-[#0A0A0A] mb-2">Donation Tier Presets</h4>
              <p className="text-[10px] text-[#4B5563] mb-3">Toggle which preset amounts appear on the RSVP donation form.</p>
              <div className="flex flex-wrap gap-2">
                {[10, 25, 50, 100, 250, 500].map(amount => {
                  const presets = eventForm.donation_presets || [10, 25, 50, 100, 250, 500];
                  const isEnabled = presets.includes(amount);
                  return (
                    <label key={amount}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border cursor-pointer text-xs font-semibold transition-colors ${
                        isEnabled
                          ? 'bg-[#BA1826] text-white border-[#BA1826]'
                          : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#BA1826]/30'
                      }`}
                      data-testid={`donation-preset-${amount}`}>
                      <input type="checkbox" checked={isEnabled}
                        onChange={(e) => {
                          const current = eventForm.donation_presets || [10, 25, 50, 100, 250, 500];
                          const updated = e.target.checked
                            ? [...current, amount].sort((a, b) => a - b)
                            : current.filter(a => a !== amount);
                          setEventForm({...eventForm, donation_presets: updated});
                        }}
                        className="sr-only" />
                      ${amount}
                    </label>
                  );
                })}
              </div>
              <div className="mt-3">
                <label className="flex items-center gap-2 text-[10px] text-[#4B5563]">
                  <input type="checkbox"
                    checked={eventForm.donation_custom_enabled !== false}
                    onChange={(e) => setEventForm({...eventForm, donation_custom_enabled: e.target.checked})}
                    className="accent-[#BA1826]"
                    data-testid="donation-custom-toggle" />
                  Allow custom amount entry
                </label>
              </div>
            </div>
          )}

          {/* ========== FUNDRAISING SETTINGS ========== */}
          <div className="border-t border-[#E5E7EB] pt-4 mt-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] mb-3 cursor-pointer">
              <input type="checkbox" checked={eventForm.fundraising?.enabled || false}
                onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, enabled: e.target.checked } }))}
                className="accent-[#BA1826]" data-testid="fundraising-enabled-checkbox" />
              Enable Fundraising Progress Bar
            </label>
            {eventForm.fundraising?.enabled && (
              <div className="bg-[#FEFCE8] border border-[#FDE68A] rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Goal Amount ($)</label>
                    <input type="number" min="0" step="1" value={eventForm.fundraising.goal_amount || 0}
                      onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, goal_amount: parseFloat(e.target.value) || 0 } }))}
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                      data-testid="fundraising-goal-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Current Amount ($) <span className="text-[#9CA3AF]">(override)</span></label>
                    <input type="number" min="0" step="0.01" value={eventForm.fundraising.current_amount || 0}
                      onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, current_amount: parseFloat(e.target.value) || 0 } }))}
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                      data-testid="fundraising-current-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Rollover Setting</label>
                    <select value={eventForm.fundraising.rollover || 'event_only'}
                      onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, rollover: e.target.value } }))}
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                      data-testid="fundraising-rollover-select">
                      <option value="event_only">Keep on event page only</option>
                      <option value="main_donation">Roll into main donation total</option>
                      <option value="global_total">Roll into global/foundation total</option>
                      <option value="archive_separately">Archive separately</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Fundraising Title</label>
                  <input type="text" value={eventForm.fundraising.title || ''}
                    onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, title: e.target.value } }))}
                    placeholder="e.g., Help Us Reach Our Goal"
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="fundraising-title-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Support Text</label>
                  <input type="text" value={eventForm.fundraising.support_text || ''}
                    onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, support_text: e.target.value } }))}
                    placeholder="Brief description of what the fundraiser supports"
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">CTA Button Text</label>
                    <input type="text" value={eventForm.fundraising.cta_text || ''}
                      onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, cta_text: e.target.value } }))}
                      placeholder="Donate Now"
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">CTA Link</label>
                    <input type="text" value={eventForm.fundraising.cta_link || ''}
                      onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, cta_link: e.target.value } }))}
                      placeholder="/donate or https://..."
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Start Date</label>
                    <input type="date" value={eventForm.fundraising.start_date || ''}
                      onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, start_date: e.target.value } }))}
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">End Date</label>
                    <input type="date" value={eventForm.fundraising.end_date || ''}
                      onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, end_date: e.target.value } }))}
                      className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={eventForm.fundraising.visible_after_event !== false}
                    onChange={(e) => setEventForm(prev => ({ ...prev, fundraising: { ...prev.fundraising, visible_after_event: e.target.checked } }))}
                    className="accent-[#BA1826]" />
                  Keep visible after event ends
                </label>
              </div>
            )}
          </div>

          {/* ========== ITEM / MERCH TRACKER ========== */}
          <div className="border-t border-[#E5E7EB] pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-[#0A0A0A]">Item / Merch Tracker</h4>
              <button type="button" onClick={() => setEventForm(prev => ({
                ...prev,
                event_items: [...(prev.event_items || []), {
                  enabled: true, name: '', image: '', goal_qty: 0, sold_qty: 0, price: 0,
                  revenue_total: 0, support_text: '', cta_text: '', cta_link: '', start_date: '', end_date: '', sold_out: false
                }]
              }))} className="text-xs text-[#BA1826] font-semibold hover:underline" data-testid="add-item-btn">
                + Add Item
              </button>
            </div>
            {(eventForm.event_items || []).length === 0 && (
              <p className="text-xs text-[#9CA3AF] mb-2">No items added. Click "+ Add Item" to track merch, raffle tickets, or any item.</p>
            )}
            {(eventForm.event_items || []).map((item, idx) => (
              <div key={idx} className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4 mb-3" data-testid={`item-editor-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 text-xs font-semibold">
                    <input type="checkbox" checked={item.enabled !== false}
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], enabled: e.target.checked };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }} className="accent-[#BA1826]" />
                    Item {idx + 1}{item.name ? `: ${item.name}` : ''}
                  </label>
                  <div className="flex items-center gap-2">
                    {idx > 0 && (
                      <button type="button" onClick={() => {
                        const items = [...eventForm.event_items];
                        [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }} className="text-[10px] text-[#4B5563] hover:text-[#0A0A0A]">Move Up</button>
                    )}
                    <button type="button" onClick={() => {
                      const items = eventForm.event_items.filter((_, i) => i !== idx);
                      setEventForm(prev => ({ ...prev, event_items: items }));
                    }} className="text-[10px] text-red-500 hover:text-red-700" data-testid={`remove-item-${idx}`}>Remove</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">Name *</label>
                    <input type="text" value={item.name || ''} placeholder="e.g., Event T-Shirt"
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], name: e.target.value };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
                      data-testid={`item-name-input-${idx}`} />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">Goal Qty</label>
                    <input type="number" min="0" value={item.goal_qty || 0}
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], goal_qty: parseInt(e.target.value) || 0 };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">Sold Qty</label>
                    <input type="number" min="0" value={item.sold_qty || 0}
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], sold_qty: parseInt(e.target.value) || 0 };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">Price ($)</label>
                    <input type="number" min="0" step="0.01" value={item.price || 0}
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], price: parseFloat(e.target.value) || 0 };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">Revenue ($)</label>
                    <input type="number" min="0" step="0.01" value={item.revenue_total || 0}
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], revenue_total: parseFloat(e.target.value) || 0 };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">Image URL</label>
                    <input type="text" value={item.image || ''} placeholder="URL"
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], image: e.target.value };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">Support Text</label>
                    <input type="text" value={item.support_text || ''} placeholder="Brief description"
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], support_text: e.target.value };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">CTA Text</label>
                    <input type="text" value={item.cta_text || ''} placeholder="Get Yours"
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], cta_text: e.target.value };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#4B5563] mb-0.5 block">CTA Link</label>
                    <input type="text" value={item.cta_link || ''} placeholder="/donate"
                      onChange={(e) => {
                        const items = [...eventForm.event_items];
                        items[idx] = { ...items[idx], cta_link: e.target.value };
                        setEventForm(prev => ({ ...prev, event_items: items }));
                      }}
                      className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={item.sold_out || false}
                    onChange={(e) => {
                      const items = [...eventForm.event_items];
                      items[idx] = { ...items[idx], sold_out: e.target.checked };
                      setEventForm(prev => ({ ...prev, event_items: items }));
                    }} className="accent-[#BA1826]" />
                  Mark as Sold Out
                </label>
              </div>
            ))}
          </div>

          {/* ========== SUGGESTED ITEMS (RSVP PAGE) ========== */}
          <div className="border-t border-[#E5E7EB] pt-4 mt-4">
            <label className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] mb-3 cursor-pointer">
              <input type="checkbox" checked={eventForm.suggested_items?.enabled || false}
                onChange={(e) => setEventForm(prev => ({
                  ...prev,
                  suggested_items: { ...(prev.suggested_items || {}), enabled: e.target.checked }
                }))}
                className="accent-[#BA1826]" data-testid="suggested-items-enabled" />
              Enable Suggested Items List
            </label>
            {eventForm.suggested_items?.enabled && (
              <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={eventForm.suggested_items?.show_on_rsvp !== false}
                    onChange={(e) => setEventForm(prev => ({
                      ...prev,
                      suggested_items: { ...prev.suggested_items, show_on_rsvp: e.target.checked }
                    }))}
                    className="accent-[#BA1826]" />
                  Show on RSVP page
                </label>
                <div>
                  <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">Header Text</label>
                  <input type="text" value={eventForm.suggested_items?.header_text || ''}
                    onChange={(e) => setEventForm(prev => ({
                      ...prev,
                      suggested_items: { ...prev.suggested_items, header_text: e.target.value }
                    }))}
                    placeholder="Guests are encouraged, but not required, to bring an item"
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="suggested-items-header" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-[#0A0A0A]">Items</span>
                  <button type="button" onClick={() => setEventForm(prev => ({
                    ...prev,
                    suggested_items: {
                      ...prev.suggested_items,
                      items: [...(prev.suggested_items?.items || []), { name: '', enabled: true }]
                    }
                  }))} className="text-xs text-[#BA1826] font-semibold hover:underline" data-testid="add-suggested-item-btn">
                    + Add Item
                  </button>
                </div>
                {(eventForm.suggested_items?.items || []).length === 0 && (
                  <p className="text-xs text-[#9CA3AF]">No suggested items yet. Click "+ Add Item" to add.</p>
                )}
                {(eventForm.suggested_items?.items || []).map((si, idx) => (
                  <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`suggested-item-${idx}`}>
                    <input type="checkbox" checked={si.enabled !== false}
                      onChange={(e) => {
                        const items = [...(eventForm.suggested_items?.items || [])];
                        items[idx] = { ...items[idx], enabled: e.target.checked };
                        setEventForm(prev => ({ ...prev, suggested_items: { ...prev.suggested_items, items } }));
                      }} className="accent-[#BA1826] flex-shrink-0" />
                    <input type="text" value={si.name || ''} placeholder="e.g., Diapers (bulk box)"
                      onChange={(e) => {
                        const items = [...(eventForm.suggested_items?.items || [])];
                        items[idx] = { ...items[idx], name: e.target.value };
                        setEventForm(prev => ({ ...prev, suggested_items: { ...prev.suggested_items, items } }));
                      }}
                      className="flex-1 min-w-[120px] px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
                      data-testid={`suggested-item-name-${idx}`} />
                    <select value={si.category || ''} onChange={(e) => {
                        const items = [...(eventForm.suggested_items?.items || [])];
                        items[idx] = { ...items[idx], category: e.target.value };
                        setEventForm(prev => ({ ...prev, suggested_items: { ...prev.suggested_items, items } }));
                      }}
                      className="px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs focus:border-[#BA1826] focus:outline-none"
                      data-testid={`suggested-item-category-${idx}`}>
                      <option value="">Category</option>
                      <option value="Hygiene">Hygiene</option>
                      <option value="Clothing">Clothing</option>
                      <option value="Baby Supplies">Baby Supplies</option>
                      <option value="Food">Food</option>
                      <option value="Other">Other</option>
                    </select>
                    <label className="flex items-center gap-1 text-[10px] text-[#4B5563] cursor-pointer flex-shrink-0"
                      title="Mark as high priority need">
                      <input type="checkbox" checked={si.high_priority || false}
                        onChange={(e) => {
                          const items = [...(eventForm.suggested_items?.items || [])];
                          items[idx] = { ...items[idx], high_priority: e.target.checked };
                          setEventForm(prev => ({ ...prev, suggested_items: { ...prev.suggested_items, items } }));
                        }} className="accent-[#BA1826]" />
                      Priority
                    </label>
                    <input type="number" min="0" value={si.goal_qty || ''} placeholder="Qty"
                      onChange={(e) => {
                        const items = [...(eventForm.suggested_items?.items || [])];
                        items[idx] = { ...items[idx], goal_qty: parseInt(e.target.value) || 0 };
                        setEventForm(prev => ({ ...prev, suggested_items: { ...prev.suggested_items, items } }));
                      }}
                      className="w-14 px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs text-center focus:border-[#BA1826] focus:outline-none"
                      title="Max quantity needed (0 = unlimited)"
                      data-testid={`suggested-item-goal-${idx}`} />
                    {idx > 0 && (
                      <button type="button" onClick={() => {
                        const items = [...(eventForm.suggested_items?.items || [])];
                        [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
                        setEventForm(prev => ({ ...prev, suggested_items: { ...prev.suggested_items, items } }));
                      }} className="text-[10px] text-[#4B5563] hover:text-[#0A0A0A] flex-shrink-0">Up</button>
                    )}
                    <button type="button" onClick={() => {
                      const items = (eventForm.suggested_items?.items || []).filter((_, i) => i !== idx);
                      setEventForm(prev => ({ ...prev, suggested_items: { ...prev.suggested_items, items } }));
                    }} className="text-[10px] text-red-500 hover:text-red-700 flex-shrink-0" data-testid={`remove-suggested-${idx}`}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={saveEvent} disabled={!eventForm.title.trim()}
            className="btn-primary text-sm disabled:opacity-50" data-testid="save-event-btn">
            <Save className="w-4 h-4" /> {editingEvent ? 'Update Event' : 'Create Event'}
          </button>
          {editingEvent && (
            <button onClick={resetEventForm} className="btn-secondary text-sm">Cancel</button>
          )}
        </div>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <p className="text-center text-[#4B5563] text-sm py-8">No events yet. Create your first event above.</p>
      ) : (
        <div className="space-y-4">
          {events.map((ev) => {
            const evStatus = ev.status || (ev.is_active ? 'active' : 'upcoming');
            return (
            <div key={ev.id} className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={`admin-event-${ev.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-heading font-bold text-sm">{ev.title}</h4>
                    {evStatus === 'completed' && <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-semibold">Completed</span>}
                    {ev.is_active && evStatus !== 'completed' && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>}
                    {!ev.is_published && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Draft</span>}
                    {ev.is_published && !ev.is_active && evStatus !== 'completed' && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Upcoming</span>}
                    <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{ev.ticket_type === 'paid' ? `$${ev.ticket_price}` : 'Free'}</span>
                  </div>
                  <p className="text-xs text-[#4B5563]">{ev.date} {ev.time && `at ${ev.time}`} {ev.location && `— ${ev.location}`}</p>
                  {ev.max_capacity > 0 && <p className="text-[10px] text-[#4B5563]">Capacity: {ev.max_capacity}</p>}
                  {ev.flyer_image && <img src={ev.flyer_image} alt="Flyer" className="w-12 h-12 object-cover rounded mt-1 border border-[#E5E7EB]" />}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {evStatus !== 'completed' && (
                    <>
                      <button onClick={() => toggleEventActive(ev)}
                        className={`text-xs px-2 py-1 rounded ${ev.is_active ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}
                        data-testid={`toggle-event-${ev.id}`}>
                        {ev.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => startCompleteEvent(ev)}
                        className="text-xs px-2 py-1 rounded bg-gray-50 text-gray-600 hover:bg-gray-100"
                        data-testid={`complete-event-${ev.id}`}>
                        <CheckCircle className="w-3.5 h-3.5 inline mr-1" />Complete
                      </button>
                      <button onClick={() => sendReminders(ev.id)}
                        className="text-xs px-2 py-1 rounded bg-gray-50 text-gray-600 hover:bg-gray-100"
                        data-testid={`remind-event-${ev.id}`}>
                        <Mail className="w-3.5 h-3.5 inline mr-1" />Remind
                      </button>
                    </>
                  )}
                  <button onClick={() => {
                    ctx.setEditingEvent(ev);
                    setEventForm({
                      title: ev.title, date: ev.date || '', time: ev.time || '', location: ev.location || '',
                      description: ev.description || '', flyer_image: ev.flyer_image || '', photos: ev.photos || [],
                      ticket_price: ev.ticket_price || 0, ticket_type: ev.ticket_type || 'free',
                      rsvp_enabled: ev.rsvp_enabled !== false, donation_items: ev.donation_items || [],
                      confirmation_message: ev.confirmation_message || '', is_active: ev.is_active, is_published: ev.is_published,
                      status: ev.status || 'upcoming', recap_text: ev.recap_text || '',
                      funds_raised: ev.funds_raised || 0, candles_lit: ev.candles_lit || 0,
                      total_attendees: ev.total_attendees || 0, donation_items_collected: ev.donation_items_collected || 0,
                      event_photos: ev.event_photos || [],
                      max_capacity: ev.max_capacity || 0, vip_price: ev.vip_price || 0,
                      donation_rsvp_enabled: ev.donation_rsvp_enabled || false, vip_rsvp_enabled: ev.vip_rsvp_enabled || false,
                      donation_presets: ev.donation_presets || [10, 25, 50, 100, 250, 500],
                      donation_custom_enabled: ev.donation_custom_enabled !== false,
                      fundraising: ev.fundraising || { enabled: false, goal_amount: 0, current_amount: 0, title: 'Help Us Reach Our Goal', support_text: '', cta_text: 'Donate Now', cta_link: '', start_date: '', end_date: '', visible_after_event: true, rollover: 'event_only' },
                      event_items: ev.event_items || [],
                      suggested_items: ev.suggested_items || { enabled: false, show_on_rsvp: true, header_text: 'Guests are encouraged, but not required, to bring an item', items: [] }
                    });
                  }} className="text-[#BA1826]" data-testid={`edit-event-${ev.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteEvent(ev.id)} className="text-red-400" data-testid={`delete-event-${ev.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Completed event stats */}
              {evStatus === 'completed' && (ev.funds_raised > 0 || ev.total_attendees > 0 || ev.candles_lit > 0 || ev.donation_items_collected > 0) && (
                <div className="grid grid-cols-4 gap-2 mb-3 mt-2">
                  <div className="text-center p-2 bg-[#F8F9FA] rounded text-xs">
                    <div className="font-bold text-[#0A0A0A]">${ev.funds_raised?.toLocaleString() || 0}</div>
                    <span className="text-[#4B5563]">Raised</span>
                  </div>
                  <div className="text-center p-2 bg-[#F8F9FA] rounded text-xs">
                    <div className="font-bold text-[#0A0A0A]">{ev.candles_lit || 0}</div>
                    <span className="text-[#4B5563]">Candles</span>
                  </div>
                  <div className="text-center p-2 bg-[#F8F9FA] rounded text-xs">
                    <div className="font-bold text-[#0A0A0A]">{ev.total_attendees || 0}</div>
                    <span className="text-[#4B5563]">Attended</span>
                  </div>
                  <div className="text-center p-2 bg-[#F8F9FA] rounded text-xs">
                    <div className="font-bold text-[#0A0A0A]">{ev.donation_items_collected || 0}</div>
                    <span className="text-[#4B5563]">Items</span>
                  </div>
                </div>
              )}
              {evStatus === 'completed' && ev.recap_text && (
                <p className="text-xs text-[#4B5563] italic mb-3 bg-[#F8F9FA] rounded p-2">{ev.recap_text}</p>
              )}

              {/* Attendee List Toggle */}
              <button onClick={() => viewingEventId === ev.id ? setViewingEventId(null) : loadAttendees(ev.id)}
                className="text-xs text-[#BA1826] font-semibold flex items-center gap-1 mt-2"
                data-testid={`view-attendees-${ev.id}`}>
                <UserCheck className="w-3.5 h-3.5" />
                {viewingEventId === ev.id ? 'Hide Attendees' : 'View Attendees'}
              </button>

              {viewingEventId === ev.id && (
                <div className="mt-4 border-t border-[#E5E7EB] pt-4">
                  {eventAttendees.length === 0 ? (
                    <p className="text-xs text-[#4B5563]">No attendees yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-[#E5E7EB] text-[#4B5563]">
                            <th className="text-left py-2 pr-3">Name</th>
                            <th className="text-left py-2 pr-3">Entry ID</th>
                            <th className="text-left py-2 pr-3">Email</th>
                            <th className="text-left py-2 pr-3">Qty</th>
                            <th className="text-left py-2 pr-3">Payment</th>
                            <th className="text-left py-2 pr-3">RSVP</th>
                            <th className="text-left py-2 pr-3">Item</th>
                            <th className="text-left py-2 pr-3">Check-in</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eventAttendees.map((att) => (
                            <tr key={att.id} className="border-b border-[#F3F4F6]">
                              <td className="py-2 pr-3 font-medium">{att.name}</td>
                              <td className="py-2 pr-3">
                                {att.entry_id ? (
                                  <span className="font-mono text-[10px] bg-[#F1F5F9] text-[#4B5563] px-1.5 py-0.5 rounded">{att.entry_id}</span>
                                ) : <span className="text-[#D1D5DB]">—</span>}
                              </td>
                              <td className="py-2 pr-3 text-[#4B5563]">{att.email}</td>
                              <td className="py-2 pr-3">{att.ticket_quantity}</td>
                              <td className="py-2 pr-3">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${getPaymentStatusColor(att.payment_status)}`}>{att.payment_status}</span>
                              </td>
                              <td className="py-2 pr-3">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${getRsvpStatusColor(att.rsvp_status)}`}>{att.rsvp_status}</span>
                              </td>
                              <td className="py-2 pr-3 text-[#4B5563] max-w-[120px] truncate">{att.selected_donation_item || '—'}</td>
                              <td className="py-2 pr-3">
                                <button onClick={() => toggleCheckIn(att)}
                                  className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${getCheckinColor(att.checked_in)}`} data-testid={`checkin-${att.id}`}>
                                  <CheckSquare className="w-3 h-3" /> {att.checked_in ? 'Yes' : 'No'}
                                </button>
                              </td>
                              <td className="py-2">
                                <button onClick={async () => {
                                  if (!window.confirm(`Delete ${att.name}? This cannot be undone.`)) return;
                                  try {
                                    await axios.delete(`${BACKEND_URL}/api/admin/events/attendees/${att.id}`, { withCredentials: true });
                                    loadAttendees(att.event_id || viewingEventId);
                                  } catch { alert('Delete failed'); }
                                }}
                                  className="text-[10px] text-red-400 hover:text-red-600 px-1"
                                  title="Delete attendee"
                                  data-testid={`delete-attendee-${att.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Send Message to Attendees */}
              <SendMessagePanel eventId={ev.id} />

              {/* RSVP Email Settings */}
              <EmailSettingsPanel eventId={ev.id} />
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminEventsTab;
